import { useCallback, useMemo } from "react";
import { useAllResourcesVisibility } from "../../../automations/common/utils/hooks/useAllResourcesVisibility.hook";
import { useAppDispatch } from "@/store/hooks";
import {
  AccountLight,
  ResourceFiltersWithAction,
  ScheduleActionType,
} from "@/services/cloudchipr.api";
import {
  initiateInDrawerWorkflowForMultiAccountsThunk,
  InitiateInDrawerWorkflowForMultiAccountsThunkArgs,
} from "@/store/automations/thunks/wokrflows/initiate/initiateInDrawerWorkflowForMultiAccountsThunk";

export type CreateWorkflowInLiveResourcesArgs = Omit<
  InitiateInDrawerWorkflowForMultiAccountsThunkArgs,
  "accountIds"
> & {
  accounts: AccountLight[];
};

export function useCreateWorkflow(
  workflowInitialData: CreateWorkflowInLiveResourcesArgs | null,
) {
  const dispatch = useAppDispatch();

  const notifyAccountsIds = useMemo(
    () => workflowInitialData?.accounts.map((account) => account.id),
    [workflowInitialData?.accounts],
  );

  const cleanAccountIds = useMemo(
    () =>
      workflowInitialData?.accounts
        .filter((account) => account.provider_access_type === "read_write")
        .map((account) => account.id),
    [workflowInitialData?.accounts],
  );

  const notifyResourcesVisibility = useAllResourcesVisibility({
    accountIds: notifyAccountsIds?.join(",") ?? "",
    provider: workflowInitialData?.provider,
  });

  const cleanResourcesVisibility = useAllResourcesVisibility({
    accountIds: cleanAccountIds?.join(",") ?? "",
    provider: workflowInitialData?.provider,
  });

  const notifyNormalizedFilters = useMemo(() => {
    return workflowInitialData?.filters?.map((filter) => {
      const actionAvailable = Object.values(notifyResourcesVisibility).every(
        (visibility) => visibility?.[filter.filter.type]?.actions.delete,
      );

      return {
        action: actionAvailable ? "terminate" : undefined,
        ...filter,
      } as ResourceFiltersWithAction;
    });
  }, [workflowInitialData, notifyResourcesVisibility]);

  const cleanNormalizedFilters = useMemo(() => {
    return workflowInitialData?.filters?.map((filter) => {
      const actionAvailable = Object.values(cleanResourcesVisibility).every(
        (visibility) => visibility?.[filter.filter.type]?.actions.delete,
      );

      return {
        action: actionAvailable ? "terminate" : undefined,
        ...filter,
      } as ResourceFiltersWithAction;
    });
  }, [workflowInitialData, cleanResourcesVisibility]);

  return useCallback(
    (action?: ScheduleActionType) => {
      dispatch(
        initiateInDrawerWorkflowForMultiAccountsThunk({
          filters:
            action === "clean_and_notify"
              ? cleanNormalizedFilters
              : notifyNormalizedFilters,
          regions: workflowInitialData?.regions,
          accountIds:
            (action === "clean_and_notify"
              ? cleanAccountIds
              : notifyAccountsIds) ?? [],
          resourceType: workflowInitialData?.resourceType,
          provider: workflowInitialData?.provider,
          action,
        }),
      );
    },
    [
      dispatch,
      workflowInitialData,
      notifyNormalizedFilters,
      cleanNormalizedFilters,
      notifyAccountsIds,
      cleanAccountIds,
    ],
  );
}
